import type { ChatMessage } from "@lib/agent/types";
import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

export function useScrollPin(viewportRef: RefObject<HTMLDivElement | null>, messages: ChatMessage[]) {
  // Ref rather than state: toggling this must not trigger re-renders, which would fire the
  // auto-scroll effect unnecessarily. Only showScrollToBottom needs to drive a render.
  const isPinnedRef = useRef(true);
  const touchStartYRef = useRef(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) {
      return;
    }

    // Un-pin on upward wheel scroll. We intentionally do NOT use the `scroll` event to un-pin:
    // `scroll` fires for both user input and programmatic scrolls, so it cannot distinguish
    // a deliberate gesture from auto-scrolling during streaming.
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        isPinnedRef.current = false;
        setShowScrollToBottom(true);
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      touchStartYRef.current = e.touches[0].clientY;
    };

    // Same reasoning as onWheel — upward swipe signals deliberate intent to read earlier content.
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches[0].clientY > touchStartYRef.current) {
        isPinnedRef.current = false;
        setShowScrollToBottom(true);
      }
    };

    // Re-pinning via `scroll` is safe in both directions: whether the scroll that brought the
    // user back to the bottom was manual or programmatic, proximity to the bottom means we
    // should resume following new content.
    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (dist < 50) {
        isPinnedRef.current = true;
        setShowScrollToBottom(false);
      }
    };

    el.addEventListener("wheel", onWheel);
    el.addEventListener("touchstart", onTouchStart);
    el.addEventListener("touchmove", onTouchMove);
    el.addEventListener("scroll", onScroll);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("scroll", onScroll);
    };
  }, [viewportRef]);

  useEffect(() => {
    // Reset pin state when the chat is cleared so the next stream auto-follows from the start.
    if (messages.length === 0) {
      isPinnedRef.current = true;
      setShowScrollToBottom(false);
      return;
    }
    if (isPinnedRef.current && viewportRef.current) {
      // No `behavior: "smooth"` — smooth scrolling during streaming causes visual jitter
      // because each new token triggers an animation. Instant jump is intentional here.
      viewportRef.current.scrollTo({ top: viewportRef.current.scrollHeight });
    }
  }, [messages, viewportRef]);

  const scrollToBottom = useCallback(() => {
    isPinnedRef.current = true;
    setShowScrollToBottom(false);
    // Smooth is appropriate here because this is an explicit user action, not a streaming update.
    viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight, behavior: "smooth" });
  }, [viewportRef]);

  return { scrollToBottom, showScrollToBottom };
}
